import { useState, type CSSProperties } from "react";
import { CaveAtmosphere } from "@client/components/cave-atmosphere";
import { PlayerStrip, type PlayerToolAction } from "@client/features/board/player-strip";
import { GameBoard } from "@client/features/board/game-board";
import { PlayerHand } from "@client/features/hand/player-hand";
import { ConnectionStatusBanner } from "@client/features/lobby/connection-status";
import { TurnControls } from "@client/features/turn-controls/turn-controls";
import { GameHeader } from "./game-header";
import { GoalPeek } from "./goal-peek";
import { useTurnInteraction } from "./use-turn-interaction";
import type { ConnectionStatus } from "@client/realtime/game-socket";
import type { GameCommand, PlayerProjection } from "@saboteur/contracts";

export function PlayerGameScreen({
  projection,
  busy,
  error,
  connectionStatus,
  submitCommand,
}: {
  projection: PlayerProjection;
  busy: boolean;
  error: string | undefined;
  connectionStatus: ConnectionStatus;
  submitCommand: (command: GameCommand) => Promise<boolean>;
}) {
  const { public: publicState, self } = projection;
  const {
    selectedCard,
    setSelectedCard,
    rotation,
    setRotation,
    pendingCommand,
    setPendingCommand,
  } = useTurnInteraction(projection);
  const [peekGoalId, setPeekGoalId] = useState<string>();
  const peekedGoal = self.inspectedGoals.find((goal) => goal.goalId === peekGoalId);
  const activeSelf = publicState.activeParticipantId === self.participantId;
  const selectedPathCard =
    selectedCard?.kind === "PATH"
      ? { cardId: selectedCard.cardId, faceId: selectedCard.faceId }
      : null;
  const sendCommand = async (command: GameCommand): Promise<void> => {
    if (await submitCommand(command)) {
      setPendingCommand(undefined);
      setSelectedCard(undefined);
      if (command.type === "turn.inspect-goal") setPeekGoalId(command.goalId);
    }
  };
  const participant = publicState.participants.find((p) => p.participantId === self.participantId)!;
  const toolAction: PlayerToolAction | undefined =
    activeSelf &&
    !busy &&
    connectionStatus === "online" &&
    (selectedCard?.kind === "BREAK_TOOL" || selectedCard?.kind === "REPAIR_TOOL")
      ? {
          kind: selectedCard.kind,
          participantIds:
            self.legalCommands.targetsByCard.find((target) => target.cardId === selectedCard.cardId)
              ?.participantIds ?? [],
          tools:
            self.legalCommands.targetsByCard.find((target) => target.cardId === selectedCard.cardId)
              ?.tools ?? [],
          onSelect: (targetParticipantId, tool) =>
            setPendingCommand({
              type: selectedCard.kind === "BREAK_TOOL" ? "turn.break-tool" : "turn.repair-tool",
              cardId: selectedCard.cardId,
              targetParticipantId,
              tool,
            }),
        }
      : undefined;
  return (
    <main
      className="game-page game-cave"
      data-round-number={publicState.roundNumber}
      onKeyDown={(event) => {
        if (event.key === "Escape") setPendingCommand(undefined);
      }}
    >
      <CaveAtmosphere />
      <GameHeader
        participant={participant}
        activeParticipant={publicState.participants.find(
          (p) => p.participantId === publicState.activeParticipantId,
        )}
        connectionStatus={connectionStatus}
        toolAction={toolAction}
        role={self.role}
        roleAssignmentKey={
          self.roleAssignmentId ?? `${publicState.roomId}:${publicState.roundNumber}`
        }
        roleReminderKey={`${publicState.roomId}:${self.participantId}`}
      />
      <ConnectionStatusBanner
        status={connectionStatus}
        waitingFor={
          publicState.participants.find(
            (p) => p.participantId === publicState.activeParticipantId && !p.connected,
          )?.displayName
        }
      />
      <PlayerStrip
        selfParticipantId={self.participantId}
        participants={publicState.participants}
        activeParticipantId={publicState.activeParticipantId}
        toolAction={toolAction}
      />
      <GameBoard
        board={publicState.board}
        participants={publicState.participants}
        activeParticipantId={publicState.activeParticipantId}
        recentActions={publicState.recentActions}
        selectedPathCard={selectedPathCard}
        legalPathPlacements={self.legalCommands.pathPlacements}
        initialRotation={rotation}
        onRotationChange={(next) => {
          setRotation(next);
          setPendingCommand(undefined);
        }}
        preview={pendingCommand?.type === "turn.play-path" ? pendingCommand : null}
        disabled={!activeSelf || busy || connectionStatus !== "online"}
        onPlacePath={(placement) => setPendingCommand({ type: "turn.play-path", ...placement })}
        removablePositions={
          selectedCard?.kind === "ROCKFALL"
            ? (self.legalCommands.targetsByCard.find((t) => t.cardId === selectedCard.cardId)
                ?.boardPositions ?? [])
            : []
        }
        onRemovePath={(position) =>
          selectedCard &&
          setPendingCommand({ type: "turn.rockfall", cardId: selectedCard.cardId, ...position })
        }
        removalPreview={pendingCommand?.type === "turn.rockfall" ? pendingCommand : null}
        inspectableGoalIds={
          selectedCard?.kind === "MAP"
            ? (self.legalCommands.targetsByCard.find((t) => t.cardId === selectedCard.cardId)
                ?.goalIds ?? [])
            : []
        }
        onInspectGoal={(goalId) =>
          selectedCard &&
          setPendingCommand({ type: "turn.inspect-goal", cardId: selectedCard.cardId, goalId })
        }
      />
      <div className="table-bottom" style={{ "--hand-count": self.hand.length } as CSSProperties}>
        {activeSelf &&
        publicState.participants.find((p) => p.participantId === self.participantId)?.brokenTools
          .length ? (
          <p className="restriction">
            Your{" "}
            {publicState.participants
              .find((p) => p.participantId === self.participantId)!
              .brokenTools.map((t) => t.toLowerCase())
              .join(" and ")}{" "}
            {publicState.participants.find((p) => p.participantId === self.participantId)!
              .brokenTools.length === 1
              ? "is"
              : "are"}{" "}
            broken. Repair or play another card.
          </p>
        ) : null}
        <TurnControls
          selectedCard={selectedCard}
          legalCommands={self.legalCommands}
          participants={publicState.participants}
          selfParticipantId={self.participantId}
          pendingCommand={pendingCommand}
          rotation={rotation}
          onRotate={() => setRotation((current) => (current === 0 ? 180 : 0))}
          busy={busy}
          disabled={!activeSelf || connectionStatus !== "online"}
          error={error}
          onPrepare={setPendingCommand}
          onCancel={() => setPendingCommand(undefined)}
          onConfirm={() => {
            if (pendingCommand) void sendCommand(pendingCommand);
          }}
        />
        <PlayerHand
          cards={self.hand}
          selectedRotation={rotation}
          selectedCardId={selectedCard?.cardId}
          playableCardIds={self.legalCommands.playableCardIds}
          disabled={busy}
          onSelect={(card) => {
            setSelectedCard(card);
            setRotation(0);
            setPendingCommand(undefined);
          }}
        />
      </div>
      <GoalPeek peekedGoal={peekedGoal} onClose={() => setPeekGoalId(undefined)} />
    </main>
  );
}
